import type { RequestConfirmationEmailByEmail } from "../../tastyworks/customer"
import type HttpClient from "../../tastyworks/http"
import {
  OTP_HEADER,
  authRequestInit,
  toJsonHelper,
} from "../../tastyworks/http"
import { recursiveDasherizeKeys } from "../../tastyworks/request"
import {
  BaseResponse,
  SingleResponse,
  parseErrorContainer,
  updateSingleResponse,
} from "../../tastyworks/response"
import TwUser, {
  USER_DESER,
  type RecaptchaClientType,
  type RecaptchaVersion,
  type SecurityQuestion,
} from "../../tastyworks/user"

interface CreateUserParams {
  email: string
  username: string
  password: string
  recaptchaToken?: string
  recaptchaVersion?: RecaptchaVersion
  recaptchaClient?: RecaptchaClientType
  securityQuestions?: SecurityQuestion[]
}

interface UpdateJointUserParams {
  password: string
  passwordConfirmation: string
  resetPasswordToken: string
  username: string
}

export class UserService {
  constructor(
    private readonly baseHttpClient: HttpClient,
    private readonly userMeClient: HttpClient
  ) {}

  async changeEmail(
    email: string,
    currentPassword: string
  ): Promise<SingleResponse<TwUser>> {
    const helper = await this.userMeClient.patch(
      "",
      recursiveDasherizeKeys({
        currentPassword,
        email,
      })
    )

    const response = new SingleResponse<TwUser>()
    parseErrorContainer(helper, response)

    return response
  }

  async requestConfirmationEmail(
    options: RequestConfirmationEmailByEmail
  ): Promise<BaseResponse> {
    const helper = await this.baseHttpClient.postJson("/confirmation", options)
    const response = new BaseResponse()
    parseErrorContainer(helper, response)
    return response
  }

  readonly create = async ({
    email,
    username,
    password,
    recaptchaToken,
    recaptchaVersion,
    recaptchaClient,
    securityQuestions,
  }: CreateUserParams): Promise<SingleResponse<TwUser>> => {
    const helper = await this.baseHttpClient.postJson(
      "/users",
      recursiveDasherizeKeys({
        email,
        username,
        password,
        recaptchaToken,
        recaptchaVersion,
        recaptchaClient,
        securityQuestions,
      })
    )

    const user = new TwUser()

    return updateSingleResponse(helper, user, USER_DESER.update)
  }

  readonly changeUsername = async (
    username: string,
    currentPassword: string
  ) => {
    const helper = await this.userMeClient.patch(
      "",
      recursiveDasherizeKeys({
        username,
        currentPassword,
      })
    )

    return updateSingleResponse(helper, new TwUser(), USER_DESER.update)
  }

  readonly updateJointUser = async (params: UpdateJointUserParams) => {
    const helper = await this.baseHttpClient.patch(
      "joint-account-users",
      recursiveDasherizeKeys(params)
    )

    const response = new SingleResponse()
    parseErrorContainer(helper, response)

    return response
  }

  readonly updateTwoFactorOnLogin = async (
    isTwoFactorSessionsEnforced: boolean,
    currentPassword: string,
    oneTimePassword: string
  ): Promise<SingleResponse<TwUser>> => {
    const url = this.userMeClient.baseUrl.toUrl("")
    // bypass session error handling
    const auth = authRequestInit(this.userMeClient)
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...auth.headers,
    }

    if (oneTimePassword) {
      headers[OTP_HEADER] = oneTimePassword
    }

    const fetchResponse = await this.userMeClient.fetcher(url, {
      body: JSON.stringify(
        recursiveDasherizeKeys({
          isTwoFactorSessionsEnforced,
          currentPassword,
        })
      ),
      credentials: auth.credentials,
      headers,
      method: "PATCH",
    })

    const helper = await toJsonHelper(fetchResponse)
    const response = new SingleResponse<TwUser>()
    parseErrorContainer(helper, response)

    return response
  }

  // retrieve the user information for the current session without causing invalid session error handling to trigger
  readonly show = async (
    sessionToken = ""
  ): Promise<SingleResponse<TwUser>> => {
    if (this.baseHttpClient.currentTransport === "bearer" && !sessionToken) {
      throw new Error("sessionToken required")
    }

    const url = this.baseHttpClient.baseUrl.toUrl("sessions/validate")
    const auth = authRequestInit(this.baseHttpClient, sessionToken)

    // circumvent session error handling
    const fetchResponse = await this.baseHttpClient.fetcher(url, {
      credentials: auth.credentials,
      headers: auth.headers,
      method: "POST",
    })

    const helper = await toJsonHelper(fetchResponse)

    return updateSingleResponse(helper, new TwUser(), USER_DESER.update)
  }
}
